import i18n from '@/i18n';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { SunMedium } from 'lucide-react';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import { useTheme } from '@/hooks/useTheme';
import {
  ForecastComparisonResponse,
  ForecastComparisonPoint,
  ForecastHalfHourResponse,
  ForecastHalfHourPoint,
  PvActualDayResponse,
  fmtKwh,
  fmtDec,
  fmtInt,
} from '@/mocks/generationForecast';

type Mode = 'hourly' | '30min';

// Кольори графіка на основі дизайн-токенів brandbook (світла/темна).
function chartColors(isDark: boolean) {
  return isDark ? {
    base: '#D23538', fact: '#FFFFFF', ecmwf: '#EA9A9C',
    v034: '#8A9199', v038: '#B65E61', v045: '#C56467',
    grid: 'rgba(255,255,255,0.08)', axis: 'rgba(255,255,255,0.12)',
    text: '#C9CCCE', muted: '#7E868E', dim: '#5E656D',
    card: '#2F3A45', tooltipBg: '#2F3A45', tooltipBorder: 'rgba(255,255,255,0.10)',
    pos: '#4CAF6A', neg: '#E5484D', zero: '#7E868E', cursor: 'rgba(255,255,255,0.12)', shadow: '0 10px 30px rgba(0,0,0,0.35)',
    cloudLow: '#C9CCCE', cloudMid: '#A6575B', cloudHigh: '#7E868E',
  } : {
    base: '#CC181B', fact: '#28323C', ecmwf: '#D9797C',
    v034: '#9AA0A6', v038: '#A6575B', v045: '#935055',
    grid: '#E4E5E7', axis: '#E4E5E7',
    text: '#28323C', muted: '#5E656D', dim: '#9AA0A6',
    card: '#FFFFFF', tooltipBg: '#FFFFFF', tooltipBorder: '#E4E5E7',
    pos: '#2E7D46', neg: '#CC181B', zero: '#9AA0A6', cursor: 'rgba(40,50,60,0.15)', shadow: '0 8px 24px rgba(40,50,60,0.08)',
    cloudLow: '#5E656D', cloudMid: '#A6575B', cloudHigh: '#9AA0A6',
  };
}


interface Props {
  comparisonData: ForecastComparisonResponse | null;
  halfHourData: ForecastHalfHourResponse | null;
  ecmwfShadowData?: { hourly: Array<{ hour: string; forecastPowerKw: number | null }> } | null;
  pvActualData: PvActualDayResponse | null;
  mode: Mode;
  dayLabel: string;
  dayDisplay: string;
  loading: boolean;
  onModeChange?: (mode: Mode) => void;
}

interface ChartRow {
  timeLabel: string;
  forecastSlot?: string | null;
  baseForecastKw: number | null;
  actualPacKw: number | null;
  v034ShadowForecastKw: number | null;
  v038ShadowForecastKw: number | null;
  ecmwfShadowForecastKw: number | null;
  baseForecastKwh: number | null;
  actualKwhApprox: number | null;
  deltaBaseKw: number | null;
  deltaBasePct: number | null;
  deltaV034Kw: number | null;
  deltaV034Pct: number | null;
  deltaV038Kw: number | null;
  deltaV038Pct: number | null;
  v038AbsErrorKw: number | null;
  baseAbsErrorKw: number | null;
  calibrationMode: string | null;
  calibrationWeight: number | null;
  bessChargeKw: number | null;
  bessDischargeKw: number | null;
  bessPowerKw: number | null;
  socPct: number | null;
  cloudCover: number | null;
  cloudCoverLow: number | null;
  cloudCoverMid: number | null;
  cloudCoverHigh: number | null;
  shortwaveRadiation: number | null;
  directRadiation: number | null;
  diffuseRadiation: number | null;
  ecmwfShortwaveRadiation: number | null;
  ecmwfDirectRadiation: number | null;
  weatherComparisonSignal: string | null;
  weatherInterpolation: string | null;
  coverage: number | null;
  qualityFlag: string | null;
  bessQualityFlag: string | null;
  deltaSeverity: string | null;
  v034CorrectionReason: string | null;
  v045HybridForecastKw: number | null;
  v045HybridSelectedSourceModel: string | null;
  v045HybridCorrectionReason: string | null;
  v045HybridMiddayRegime: string | null;
}

type Visibility = {
  base: boolean;
  actual: boolean;
  v034: boolean;
  v038: boolean;
  v045: boolean;
  ecmwfShadow: boolean;
};

function buildHourlyRows(data: ForecastComparisonResponse | null): ChartRow[] {
  if (!data?.points?.length) {
    return Array.from({ length: 24 }, (_, i) => ({
      timeLabel: `${String(i).padStart(2, '0')}:00`,
      baseForecastKw: null,
      actualPacKw: null,
      v034ShadowForecastKw: null,
      v038ShadowForecastKw: null,
      ecmwfShadowForecastKw: null,
      baseForecastKwh: null,
      actualKwhApprox: null,
      deltaBaseKw: null,
      deltaBasePct: null,
      deltaV034Kw: null,
      deltaV034Pct: null,
      deltaV038Kw: null,
      deltaV038Pct: null,
      v038AbsErrorKw: null,
      baseAbsErrorKw: null,
      calibrationMode: null,
      calibrationWeight: null,
      bessChargeKw: null,
      bessDischargeKw: null,
      bessPowerKw: null,
      socPct: null,
      cloudCover: null,
      cloudCoverLow: null,
      cloudCoverMid: null,
      cloudCoverHigh: null,
      shortwaveRadiation: null,
      directRadiation: null,
      diffuseRadiation: null,
      ecmwfShortwaveRadiation: null,
      ecmwfDirectRadiation: null,
      weatherComparisonSignal: null,
      weatherInterpolation: null,
      coverage: null,
      qualityFlag: null,
      bessQualityFlag: null,
      deltaSeverity: null,
      v034CorrectionReason: null,
      v038CorrectionReason: null,
      v045HybridForecastKw: null,
      v045HybridSelectedSourceModel: null,
      v045HybridCorrectionReason: null,
      v045HybridMiddayRegime: null,
    }));
  }

  return data.points.map((p: ForecastComparisonPoint) => ({
    timeLabel: p.forecastHour ?? p.hour,
    forecastSlot: null,
    // Base line = GENUINE base v0.2.2 (trueBaseForecastKw), not the routed
    // preferred alias — otherwise it duplicates the ECMWF line for assets
    // routed to ECMWF (e.g. Tabaki). Fallback to routed value if absent.
    baseForecastKw: (p as any).trueBaseForecastKw ?? p.baseForecastKw,
    actualPacKw: p.actualPacKw,
    v034ShadowForecastKw: p.v034ShadowForecastKw,
    v038ShadowForecastKw: p.v038ShadowForecastKw,
    // Genuine ECMWF from comparison endpoint (distinct from base now).
    ecmwfShadowForecastKw: (p as any).ecmwfForecastKw ?? null,
    baseForecastKwh: null,
    actualKwhApprox: null,
    deltaBaseKw: p.deltaBaseKw,
    deltaBasePct: p.deltaBasePct,
    deltaV034Kw: p.deltaV034Kw,
    deltaV034Pct: p.deltaV034Pct,
    deltaV038Kw: p.deltaV038Kw,
    deltaV038Pct: p.deltaV038Pct,
    v038AbsErrorKw: p.v038AbsErrorKw,
    baseAbsErrorKw: null,
    calibrationMode: p.calibrationMode,
    calibrationWeight: null,
    bessChargeKw: p.bessChargeKw,
    bessDischargeKw: p.bessDischargeKw,
    bessPowerKw: null,
    socPct: p.socPct,
    cloudCover: p.cloudCover,
    cloudCoverLow: p.cloudCoverLow ?? null,
    cloudCoverMid: p.cloudCoverMid ?? null,
    cloudCoverHigh: p.cloudCoverHigh ?? null,
    shortwaveRadiation: p.shortwaveRadiation,
    directRadiation: p.directRadiation,
    diffuseRadiation: null,
    ecmwfShortwaveRadiation: p.ecmwfShortwaveRadiation,
    ecmwfDirectRadiation: p.ecmwfDirectRadiation,
    weatherComparisonSignal: p.weatherComparisonSignal,
    weatherInterpolation: null,
    coverage: null,
    qualityFlag: null,
    bessQualityFlag: null,
    deltaSeverity: null,
    v034CorrectionReason: p.v034CorrectionReason,
    v038CorrectionReason: p.v038CorrectionReason,
    v045HybridForecastKw: p.v045HybridForecastKw,
    v045HybridSelectedSourceModel: p.v045HybridSelectedSourceModel,
    v045HybridCorrectionReason: p.v045HybridCorrectionReason,
    v045HybridMiddayRegime: p.v045HybridMiddayRegime,
  }));
}

function buildHalfHourRows(
  data: ForecastHalfHourResponse | null,
  hourlyFallback?: PvActualDayResponse | null,
): ChartRow[] {
  if (!data?.points?.length) {
    return Array.from({ length: 48 }, (_, i) => {
      const h = Math.floor(i / 2);
      const m = i % 2 === 0 ? '00' : '30';
      return {
        timeLabel: `${String(h).padStart(2, '0')}:${m}`,
        forecastSlot: null,
        baseForecastKw: null,
        actualPacKw: null,
        v034ShadowForecastKw: null,
        v038ShadowForecastKw: null,
      ecmwfShadowForecastKw: null,
        baseForecastKwh: null,
        actualKwhApprox: null,
        deltaBaseKw: null,
        deltaBasePct: null,
        deltaV034Kw: null,
        deltaV034Pct: null,
        deltaV038Kw: null,
        deltaV038Pct: null,
        v038AbsErrorKw: null,
        baseAbsErrorKw: null,
        calibrationMode: null,
        calibrationWeight: null,
        bessChargeKw: null,
        bessDischargeKw: null,
        bessPowerKw: null,
        socPct: null,
        cloudCover: null,
        cloudCoverLow: null,
        cloudCoverMid: null,
        cloudCoverHigh: null,
        shortwaveRadiation: null,
        directRadiation: null,
        diffuseRadiation: null,
        ecmwfShortwaveRadiation: null,
        ecmwfDirectRadiation: null,
        weatherComparisonSignal: null,
        weatherInterpolation: null,
        coverage: null,
        qualityFlag: null,
        bessQualityFlag: null,
        deltaSeverity: null,
        v034CorrectionReason: null,
        v038CorrectionReason: null,
        v045HybridForecastKw: null,
        v045HybridSelectedSourceModel: null,
        v045HybridCorrectionReason: null,
        v045HybridMiddayRegime: null,
      };
    });
  }

  // Build hourly actual lookup for fallback: hour string -> actualPacKw
  const hourlyMap = new Map<string, number | null>();
  if (hourlyFallback?.actual?.length) {
    for (const h of hourlyFallback.actual) {
      if (h.hour != null) {
        hourlyMap.set(h.hour, h.actualPacKw);
      }
    }
  }

  return data.points.map((p: ForecastHalfHourPoint) => {
    let actualKw = p.actualPacKw;
    // Fallback to hourly actual if half-hour actual is null but hourly exists.
    // Zero is a valid reading; only null/undefined means missing.
    if (actualKw === null || actualKw === undefined) {
      const hourKey = p.time.substring(0, 5); // "HH:MM"
      const fallback = hourlyMap.get(hourKey);
      if (fallback !== null && fallback !== undefined) {
        actualKw = fallback;
      }
    }

    return {
      timeLabel: p.time,
      forecastSlot: p.forecastSlot,
      // GENUINE base (v0.2.2), not the v0.4.0-30min routed value — the routed
      // value equals ECMWF for ECMWF-routed assets, which made base overlap ECMWF.
      baseForecastKw: (p as any).trueBaseForecastKw ?? p.baseForecastKw,
      actualPacKw: actualKw,
      v034ShadowForecastKw: null,
      v038ShadowForecastKw: null,
      ecmwfShadowForecastKw: null,
      baseForecastKwh: p.baseForecastKwh,
      actualKwhApprox: p.actualKwhApprox,
      deltaBaseKw: p.deltaBaseKw,
      deltaBasePct: p.deltaBasePct,
      deltaV034Kw: null,
      deltaV034Pct: null,
      deltaV038Kw: null,
      deltaV038Pct: null,
      v038AbsErrorKw: null,
      baseAbsErrorKw: p.baseAbsErrorKw,
      calibrationMode: p.calibrationMode,
      calibrationWeight: p.calibrationWeight,
      bessChargeKw: p.bessChargeKw,
      bessDischargeKw: p.bessDischargeKw,
      bessPowerKw: p.bessPowerKw,
      socPct: p.socPct,
      cloudCover: p.cloudCover,
      cloudCoverLow: (p as any).cloudCoverLow ?? null,
      cloudCoverMid: (p as any).cloudCoverMid ?? null,
      cloudCoverHigh: (p as any).cloudCoverHigh ?? null,
      shortwaveRadiation: p.shortwaveRadiation,
      directRadiation: p.directRadiation,
      diffuseRadiation: p.diffuseRadiation,
      ecmwfShortwaveRadiation: null,
      ecmwfDirectRadiation: null,
      weatherComparisonSignal: null,
      weatherInterpolation: p.weatherInterpolation,
      coverage: p.coverage,
      qualityFlag: p.qualityFlag,
      bessQualityFlag: p.bessQualityFlag,
      deltaSeverity: p.deltaSeverity,
      v034CorrectionReason: null,
      v038CorrectionReason: null,
      v045HybridForecastKw: null,
      v045HybridSelectedSourceModel: null,
      v045HybridCorrectionReason: null,
      v045HybridMiddayRegime: null,
    };
  });
}

const HourlyTooltip = ({ row }: { row: ChartRow }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const C = chartColors(isDark);
  const base = row.baseForecastKw;
  const actual = row.actualPacKw;

  const deltaBaseColor =
    row.deltaBaseKw == null ? C.muted
      : row.deltaBaseKw > 0 ? C.pos
      : row.deltaBaseKw < 0 ? C.neg : C.zero;

  const hasCloudBreakdown = row.cloudCoverLow != null || row.cloudCoverMid != null || row.cloudCoverHigh != null;

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between gap-4">
        <span style={{ color: C.muted }}>Base forecast, kW</span>
        <span className="font-medium" style={{ color: base != null ? C.base : C.muted }}>
          {base != null ? `${fmtKwh(base)}` : '—'}
        </span>
      </div>
      <div className="flex justify-between gap-4">
        <span style={{ color: C.muted }}>{t('fc_tt_actual_pv_kw')}</span>
        <span className="font-medium" style={{ color: actual != null ? C.fact : C.muted }}>
          {actual != null ? `${fmtKwh(actual)}` : '—'}
        </span>
      </div>
      {row.ecmwfShadowForecastKw != null && (
        <div className="flex justify-between gap-4">
          <span style={{ color: C.muted }}>ECMWF_ifs025, kW</span>
          <span className="font-medium" style={{ color: C.ecmwf }}>{fmtKwh(row.ecmwfShadowForecastKw)}</span>
        </div>
      )}

      {(row.deltaBaseKw != null || row.deltaBasePct != null) && (
        <>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '6px 0' }} />
          <div className="flex justify-between gap-4">
            <span style={{ color: C.muted }}>{t('fc_tt_delta_vs_base_kw')}</span>
            <span className="font-medium" style={{ color: deltaBaseColor }}>
              {row.deltaBaseKw != null ? `${row.deltaBaseKw > 0 ? '+' : ''}${fmtKwh(row.deltaBaseKw)}` : '—'}
            </span>
          </div>
          <div className="flex justify-between gap-4">
            <span style={{ color: C.muted }}>{t('fc_tt_delta_vs_base_pct')}</span>
            <span className="font-medium" style={{ color: deltaBaseColor }}>
              {row.deltaBasePct != null ? `${row.deltaBasePct > 0 ? '+' : ''}${fmtDec(row.deltaBasePct, 1)}%` : '—'}
            </span>
          </div>
        </>
      )}

      {(row.bessChargeKw != null || row.bessDischargeKw != null || row.socPct != null) && (
        <>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '6px 0' }} />
          {row.calibrationMode && (
            <div className="flex justify-between gap-4">
              <span style={{ color: C.muted }}>{t('fc_tt_calibration')}</span>
              <span className="font-medium text-ink">{row.calibrationMode}</span>
            </div>
          )}
          {row.bessChargeKw != null && row.bessChargeKw > 0 && (
            <div className="flex justify-between gap-4">
              <span style={{ color: C.muted }}>{t('fc_tt_bess_charge')}</span>
              <span className="font-medium" style={{ color: C.fact }}>{fmtKwh(row.bessChargeKw)}</span>
            </div>
          )}
          {row.bessDischargeKw != null && row.bessDischargeKw > 0 && (
            <div className="flex justify-between gap-4">
              <span style={{ color: C.muted }}>{t('fc_tt_bess_discharge')}</span>
              <span className="font-medium" style={{ color: C.v038 }}>{fmtKwh(row.bessDischargeKw)}</span>
            </div>
          )}
          {row.socPct != null && (
            <div className="flex justify-between gap-4">
              <span style={{ color: C.muted }}>SOC, %</span>
              <span className="font-medium text-ink">{`${fmtInt(row.socPct)}%`}</span>
            </div>
          )}
        </>
      )}

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '6px 0' }} />
      <div className="flex justify-between gap-4">
        <span style={{ color: C.muted }}>{t('fc_tt_cloud_pct')}</span>
        <span className="font-medium text-ink">{row.cloudCover != null ? `${fmtDec(row.cloudCover, 1)}%` : '—'}</span>
      </div>
      {hasCloudBreakdown && (
        <div className="flex justify-between gap-4">
          <span style={{ color: C.muted }}>{t('fc_tt_cloud_lmh')}</span>
          <span className="font-medium" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
            <span style={{ color: C.cloudLow }}>{row.cloudCoverLow != null ? Math.round(row.cloudCoverLow) : '–'}</span>
            {' / '}
            <span style={{ color: C.cloudMid }}>{row.cloudCoverMid != null ? Math.round(row.cloudCoverMid) : '–'}</span>
            {' / '}
            <span style={{ color: C.cloudHigh }}>{row.cloudCoverHigh != null ? Math.round(row.cloudCoverHigh) : '–'}</span>
          </span>
        </div>
      )}
      <div className="flex justify-between gap-4">
        <span style={{ color: C.muted }}>{t('fc_tt_shortwave')}</span>
        <span className="font-medium text-ink">
          {row.shortwaveRadiation != null ? `${fmtInt(row.shortwaveRadiation)} W/m²` : '—'}
        </span>
      </div>
      <div className="flex justify-between gap-4">
        <span style={{ color: C.muted }}>{t('fc_tt_direct')}</span>
        <span className="font-medium text-ink">
          {row.directRadiation != null ? `${fmtInt(row.directRadiation)} W/m²` : '—'}
        </span>
      </div>


    </div>
  );
};

const HalfHourTooltip = ({ row }: { row: ChartRow }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const C = chartColors(isDark);
  const base = row.baseForecastKw;
  const actual = row.actualPacKw;
  const deltaColor = row.deltaBaseKw == null ? C.muted
    : row.deltaBaseKw > 0 ? C.pos : row.deltaBaseKw < 0 ? C.neg : C.zero;
  const Cell = ({ label, value, color }: { label: string; value: string; color: string }) => (
    <div className="flex justify-between gap-4">
      <span style={{ color: C.muted }}>{label}</span>
      <span className="font-medium" style={{ color }}>{value}</span>
    </div>
  );
  const cLow = row.cloudCoverLow != null ? String(fmtInt(row.cloudCoverLow)) : '–';
  const cMid = row.cloudCoverMid != null ? String(fmtInt(row.cloudCoverMid)) : '–';
  const cHigh = row.cloudCoverHigh != null ? String(fmtInt(row.cloudCoverHigh)) : '–';
  const deltaStr = row.deltaBaseKw != null
    ? `${row.deltaBaseKw > 0 ? '+' : ''}${fmtKwh(row.deltaBaseKw)} kW${row.deltaBasePct != null ? ` (${row.deltaBasePct > 0 ? '+' : ''}${fmtDec(row.deltaBasePct, 1)}%)` : ''}`
    : '—';
  const divider = { borderTop: '1px solid rgba(255,255,255,0.08)', margin: '6px 0' };
  return (
    <div className="space-y-1.5">
      <Cell label="Base Forecast" value={base != null ? `${fmtKwh(base)} kW` : '—'} color={base != null ? C.base : C.muted} />
      <Cell label={t('fc_series_actual')} value={actual != null ? `${fmtKwh(actual)} kW` : '—'} color={actual != null ? C.fact : C.muted} />
      <Cell label="ECMWF" value={row.ecmwfShadowForecastKw != null ? `${fmtKwh(row.ecmwfShadowForecastKw)} kW` : '—'} color={row.ecmwfShadowForecastKw != null ? C.ecmwf : C.muted} />
      <div style={divider} />
      <Cell label="Δ" value={deltaStr} color={deltaColor} />
      <div style={divider} />
      <Cell label={t('ak_fc_radiation_min_corrected')} value={row.shortwaveRadiation != null ? `${fmtInt(row.shortwaveRadiation)} W/m²` : '—'} color={C.text} />
      <Cell label={t('ak_fc_cloud_cover')} value={row.cloudCover != null ? `${fmtInt(row.cloudCover)}%` : '—'} color={C.text} />
      <div className="flex justify-between gap-4">
        <span style={{ color: C.muted }}>{t('fc_tt_cloud_lmh')}</span>
        <span className="font-medium" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          <span style={{ color: C.cloudLow }}>{cLow}</span>
          <span style={{ color: C.muted }}>{' / '}</span>
          <span style={{ color: C.cloudMid }}>{cMid}</span>
          <span style={{ color: C.muted }}>{' / '}</span>
          <span style={{ color: C.cloudHigh }}>{cHigh}</span>
        </span>
      </div>
    </div>
  );
};
const CustomTooltip = ({ active, payload }: any) => {
  const { isDark } = useTheme();
  const C = chartColors(isDark);
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as ChartRow;
  if (!row) return null;

  const isHalfHour = row.forecastSlot != null || row.weatherInterpolation != null;

  return (
    <div
      className="rounded-xl px-4 py-3 text-sm"
      style={{
        background: C.tooltipBg,
        border: '1px solid rgba(255,255,255,0.12)',
        minWidth: 200,
        maxWidth: 280,
      }}
    >
      <div className="font-semibold text-ink mb-2" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>
        {row.timeLabel}
      </div>
      {isHalfHour ? <HalfHourTooltip row={row} /> : <HourlyTooltip row={row} />}
    </div>
  );
};

export default function ForecastMainChart({
  comparisonData,
  halfHourData,
  ecmwfShadowData,
  pvActualData,
  mode,
  dayLabel,
  dayDisplay,
  loading,
  onModeChange,
}: Props) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const C = chartColors(isDark);
  // v0.2.3 cleanup: hide v0.3.4 (legacy) and ECMWF adjusted (already folded
  // into v0.4.5 Hybrid). Toggle chips still available for investigation.
  // Persisted toggle state. localStorage key is shared across all assets/dates
  // because the choice "which forecast lines to show" is a personal preference.
  const VISIBILITY_LS_KEY = 'helios.forecast.visibility.v1';
  const DEFAULT_VISIBILITY: Visibility = {
    base: true,
    actual: true,
    v034: false,
    v038: false,
    v045: false,
    ecmwfShadow: true,
  };
  const [visible, setVisibleRaw] = useState<Visibility>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(VISIBILITY_LS_KEY) : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        // Merge with defaults so new keys (added later) don't crash old saves
        return { ...DEFAULT_VISIBILITY, ...parsed };
      }
    } catch { /* ignore corrupt JSON */ }
    return DEFAULT_VISIBILITY;
  });
  const setVisible: typeof setVisibleRaw = (updater) => {
    setVisibleRaw((prev) => {
      const next = typeof updater === 'function' ? (updater as (v: Visibility) => Visibility)(prev) : updater;
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(VISIBILITY_LS_KEY, JSON.stringify(next));
        }
      } catch { /* ignore quota errors */ }
      return next;
    });
  };
  const { selectedAsset } = useSelectedAsset();

  const isHalfHour = true; // селектор прибрано: завжди 30-хв дані, назва «Погодинне»

  // Visual effective ceiling for forecast lines — combines operational capacity
  // (equipment degradation) AND active curtailment cap (OSR/SKO commands), taking
  // the LOWER of the two. Lines never display above what the plant can physically
  // (or commercially) output today. Backend keeps raw values for audit / loss reports.
  const effectiveCeilingKw = (() => {
    const opMw = (comparisonData as any)?.operationalCapacityMw
      ?? (halfHourData as any)?.operationalCapacityMw;
    const opCapKw = typeof opMw === 'number' && opMw > 0 ? opMw * 1000 : null;
    const osrCapRaw = (comparisonData as any)?.activeCurtailment?.curtailmentPmaxKw
      ?? (halfHourData as any)?.activeCurtailment?.curtailmentPmaxKw;
    const osrCapKw = typeof osrCapRaw === 'number' && osrCapRaw > 0 ? osrCapRaw : null;
    if (opCapKw !== null && osrCapKw !== null) return Math.min(opCapKw, osrCapKw);
    return opCapKw ?? osrCapKw ?? null;
  })();
  const clipToCeiling = (v: number | null | undefined): number | null => {
    if (v == null || !Number.isFinite(Number(v))) return null;
    const x = Number(v);
    return effectiveCeilingKw !== null && x > effectiveCeilingKw ? effectiveCeilingKw : x;
  };

  // Bridge ONLY short actual-telemetry gaps (<= maxGap slots) by linear
  // interpolation, leaving longer gaps as null so the line breaks honestly
  // (connectNulls stays false — true would draw absurd straight lines across
  // whole-day gaps for stations with sparse telemetry).
  const fillShortActualGaps = (rows: ChartRow[], maxGap: number): ChartRow[] => {
    const n = rows.length;
    let i = 0;
    while (i < n) {
      if (rows[i].actualPacKw == null) {
        let j = i;
        while (j < n && rows[j].actualPacKw == null) j++;
        const b = i - 1;
        if (b >= 0 && j < n && rows[b].actualPacKw != null && rows[j].actualPacKw != null && (j - i) <= maxGap) {
          const v0 = rows[b].actualPacKw as number;
          const v1 = rows[j].actualPacKw as number;
          const span = j - b;
          for (let m = i; m < j; m++) {
            rows[m] = { ...rows[m], actualPacKw: v0 + (v1 - v0) * ((m - b) / span) };
          }
        }
        i = j;
      } else { i++; }
    }
    return rows;
  };

  const data: ChartRow[] = useMemo(() => {
    let rows: ChartRow[];
    // ОБИДВА режими будуємо з ЄДИНОГО вирівняного 30-хв джерела (halfHourData),
    // щоб вісь X була однаковою (00:00..23:30) і графік НЕ стрибав при переключенні
    // (раніше годинний брав comparison/day з міткою +1 год -> зсув осі та піку).
    // Прогноз годинний (Open-Meteo hourly) -> у обох режимах годинні полички-трапеції.
    rows = buildHalfHourRows(halfHourData, pvActualData);
    {
      // Факт -> годинні полички з ТОГО Ж джерела, що таблиця/KPI/чіп (pvActualData
      // погодинне репрезентативне значення), одне значення на обидва :00/:30 слоти.
      // Раніше тут усереднювались РІЗНОРІДНІ семпли: :00-слот (годинний fallback)
      // + :30-слот (миттєвий снапшот з half-hour ендпоінта) -> тултіп показував
      // (536.5+740.7)/2=638.6, що не збігалося з таблицею. Тепер джерело єдине.
      const _hourActual = new Map<string, number>();
      const _acts = (pvActualData as any)?.actual as any[] | undefined;
      if (_acts) {
        for (const a of _acts) {
          const mm = String(a?.hour ?? '').match(/^(\d{2}):/);
          const v = Number(a?.actualPacKw);
          if (mm && Number.isFinite(v)) _hourActual.set(mm[1], v);
        }
      }
      rows = rows.map((r) => {
        const mm = (r.timeLabel ?? '').match(/^(\d{2}):/);
        return (mm && _hourActual.has(mm[1]))
          ? { ...r, actualPacKw: _hourActual.get(mm[1]) as number }
          : r;
      });
    }
    rows = fillShortActualGaps(rows, isHalfHour ? 4 : 2);
    if (effectiveCeilingKw !== null) {
      rows = rows.map((r) => ({
        ...r,
        baseForecastKw: clipToCeiling(r.baseForecastKw),
      }));
    }
    // Merge ECMWF shadow forecast by hour key.
    // hourly mode: rows have HH:00 labels — direct lookup.
    // 30-min mode: rows have HH:00 AND HH:30 labels — both should pick up
    // the same hourly ECMWF value (we don't have native 30-min ECMWF; we
    // duplicate the hourly forecast across both 30-min slots, mirroring the
    // expanded-from-hourly behaviour for v0.2.2-base in /forecast-comparison/half-hour).
    if (ecmwfShadowData?.hourly?.length) {
      const shadowMap = new Map<string, number>();
      for (const r of ecmwfShadowData.hourly) {
        if (r.forecastPowerKw != null) {
          shadowMap.set(r.hour, Number(r.forecastPowerKw));
          // Also register HH:30 → same hour value so 30-min rows resolve.
          const m = r.hour.match(/^(\d{2}):00$/);
          if (m) shadowMap.set(`${m[1]}:30`, Number(r.forecastPowerKw));
        }
      }
      rows = rows.map((row) => ({
        ...row,
        ecmwfShadowForecastKw: (() => {
          const lbl = row.timeLabel ?? '';
          const m = lbl.match(/(\d{2}:\d{2})/);
          const key = m ? m[1] : '';
          const raw = shadowMap.get(key) ?? null;
          return clipToCeiling(raw);
        })(),
      }));
    }
    return rows;
  }, [isHalfHour, halfHourData, comparisonData, pvActualData, ecmwfShadowData]);

  const totalSlots = data.length;

  const summary = useMemo(() => {
    if (isHalfHour) {
      return halfHourData?.summary ?? null;
    }
    return comparisonData?.summary ?? null;
  }, [isHalfHour, halfHourData, comparisonData]);

  const maxVal = useMemo(() => {
    // Only series that are actually rendered should drive the Y-axis scale —
    // otherwise a hidden series (e.g. the deprecated v0.4.5 hybrid, which carries
    // an unclipped nameplate-level forecast) inflates the axis and squashes the
    // visible OSR-capped lines. The conditions here mirror the <Area> render gates.
    const all = data
      .flatMap((d) => {
        const vals: (number | null | undefined)[] = [];
        if (visible.base) vals.push(d.baseForecastKw);
        if (visible.actual) vals.push(d.actualPacKw);
        if (!isHalfHour && visible.v034) vals.push(d.v034ShadowForecastKw);
        if (!isHalfHour && visible.v038) vals.push(d.v038ShadowForecastKw);
        if (!isHalfHour && visible.v045) vals.push(d.v045HybridForecastKw);
        if (visible.ecmwfShadow) vals.push(d.ecmwfShadowForecastKw);
        return vals;
      })
      .filter((v): v is number => v != null && Number.isFinite(v));
    if (all.length === 0) return 5;
    return Math.max(...all);
  }, [data, visible, isHalfHour]);

  // 30-min mode empty-state: if no point carries baseForecastKw, the v040
  // generator is offline. Show a "switch to hourly" prompt instead of a
  // blank chart.
  const hasAnyHalfHourForecast = useMemo(() => {
    if (!isHalfHour) return true;
    return data.some((d) => d.baseForecastKw != null);
  }, [isHalfHour, data]);

  const yMax = useMemo(() => {
    if (maxVal <= 0) return 5;
    return parseFloat((Math.ceil(maxVal * 10) / 10 + 0.3).toFixed(1));
  }, [maxVal]);

  const availableCount = useMemo(
    () => data.filter((d) => d.baseForecastKw != null).length,
    [data],
  );
  const actualCount = useMemo(
    () => data.filter((d) => d.actualPacKw !== null && d.actualPacKw !== undefined).length,
    [data],
  );

  // Накопичена фактично згенерована енергія за добу (МВт·год) до поточної години.
  // 30-хв слот = 0.5 год; сума по слотах з фактом = кумулятив від початку доби.
  // Природно «обнуляється» на новій добі: дані фетчаться на обрану дату, вночі факт≈0,
  // тож о 00:0x нова доба стартує з ~0 і накопичується протягом дня.
  const { genTodayMwh, genUpdatedAt } = useMemo(() => {
    // Факт за добу з ТОГО Ж джерела, що й таблиця «Σ факт» (pvActualData.actual):
    // Σ погодинної потужності ≈ погодинна енергія -> МВт·год. Лічильник = таблиці.
    const acts = (pvActualData as any)?.actual as any[] | undefined;
    let kwh = 0;
    if (acts && acts.length) {
      for (const a of acts) { const v = Number(a?.actualPacKw); if (Number.isFinite(v)) kwh += v; }
    }
    const updatedAt = new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Kyiv' });
    return { genTodayMwh: kwh / 1000, genUpdatedAt: updatedAt };
  }, [pvActualData]);

  // X-axis interval: adaptive to avoid overcrowding
  const xInterval = useMemo(() => {
    if (totalSlots <= 24) return 2;
    if (totalSlots <= 48) return 3;
    return 5;
  }, [totalSlots]);

  if (loading) {
    return (
      <div
        className="rounded-xl p-5 flex items-center justify-center"
        style={{ background: C.card, border: '1px solid ' + C.axis, boxShadow: C.shadow, borderRadius: 14, minHeight: 360 }}
      >
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: C.muted }}></i>
          <p className="text-sm mt-3" style={{ color: C.muted }}>
            {isHalfHour ? t('fc_loading_30min_cmp') : t('fc_loading_hourly_cmp')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl p-5"
      style={{ background: C.card, border: '1px solid ' + C.axis, boxShadow: C.shadow, borderRadius: 14 }}
    >
      {/* Header with mode switch */}
      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-start gap-4 flex-wrap">
        <div>
          <div className="text-ink font-semibold text-base" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>
            {t('fc_title_hourly')}
          </div>
          <div className="text-xs mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: C.muted }}>
            <span>
              {dayLabel} · {dayDisplay}
              {selectedAsset ? ` · ${selectedAsset.assetName} (${selectedAsset.installedCapacityMw} ${i18n.t('ak_fc_mw')})` : ''}
            </span>
            {/* бейдж «Активна: ...» прибрано */}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {([
            { key: 'base', label: 'Base Forecast', color: C.base },
            { key: 'actual', label: t('fc_series_actual'), color: C.fact },
            { key: 'ecmwfShadow', label: 'ECMWF', color: C.ecmwf },
          ] as const).map((c) => {
            const on = (visible as any)[c.key];
            return (
              <button
                key={c.key}
                onClick={() => setVisible((vv) => ({ ...vv, [c.key]: !(vv as any)[c.key] }))}
                title={on ? t('ak_fc_hide_series_on_chart') : t('ak_fc_show_series_on_chart')}
                className="flex items-center justify-center gap-1.5 rounded-md text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
                style={{
                  minWidth: 140,
                  padding: '6px 14px',
                  background: c.color + '1F',
                  color: C.text,
                  border: '1px solid ' + c.color + '55',
                  opacity: on ? 1 : 0.4,
                }}
              >
                <div className="w-2 h-2 rounded-full" style={{ background: c.color }}></div>
                {c.label}
              </button>
            );
          })}
        </div>
        </div>
        <div
          className="flex items-center gap-1.5 rounded-md text-xs whitespace-nowrap"
          style={{ border: '1px solid rgba(255,255,255,0.1)', background: C.grid, padding: '6px 14px' }}
        >
          <SunMedium size={15} strokeWidth={1.75} style={{ color: C.muted }} />
          <span className="font-bold" style={{ color: C.fact, fontSize: 14 }}>{fmtDec(genTodayMwh, 3)} {t('ak_fc_mwh')}</span>
          <span style={{ color: C.muted }}>· {genUpdatedAt}</span>
        </div>
      </div>

      {/* Chart */}
      {!hasAnyHalfHourForecast ? (
        <div
          className="flex flex-col items-center justify-center text-center rounded-xl"
          style={{ minHeight: 380, background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)' }}
        >
          <div className="text-base font-semibold mb-1" style={{ color: C.muted }}>{t('fc_empty_30min_title')}</div>
          <div className="text-xs mb-4" style={{ color: C.muted, maxWidth: 360 }}>{t('fc_empty_30min_body')}</div>
          {onModeChange && (
            <button
              onClick={() => onModeChange('hourly')}
              className="px-4 py-2 rounded-md text-xs font-medium"
              style={{ background: C.base, color: '#fff' }}
            >{t('fc_switch_hourly')}</button>
          )}
        </div>
      ) : (<ResponsiveContainer width="100%" height={320}>
        <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gradBase" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={C.base} stopOpacity={0.25} />
              <stop offset="95%" stopColor={C.base} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="gradActual" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={C.fact} stopOpacity={0.18} />
              <stop offset="95%" stopColor={C.fact} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="gradV034" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={C.v034} stopOpacity={0.12} />
              <stop offset="95%" stopColor={C.v034} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="gradV038" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={C.v038} stopOpacity={0.18} />
              <stop offset="95%" stopColor={C.v038} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="gradV045" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={C.ecmwf} stopOpacity={0.18} />
              <stop offset="95%" stopColor={C.ecmwf} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
          <XAxis
            dataKey="timeLabel"
            tick={{ fill: C.muted, fontSize: 11, fontFamily: 'Urbanist, Inter, sans-serif' }}
            tickLine={false}
            axisLine={{ stroke: C.axis }}
            interval={1}
            minTickGap={20}
            tickFormatter={(label: string) => {
              const match = label.match(/(\d{1,2}:\d{2})/);
              return match ? match[1] : label;
            }}
          />
          <YAxis
            domain={[0, yMax]}
            tick={{ fill: C.muted, fontSize: 11, fontFamily: 'Urbanist, Inter, sans-serif' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `${v}`}
            width={42}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: C.cursor, strokeWidth: 1 }} />

          {visible.base && (
            <Area
              type="linear"
              dataKey="baseForecastKw"
              stroke={C.base}
              strokeWidth={2}
              fill="url(#gradBase)"
              dot={false}
              activeDot={{ r: 4, fill: C.base, stroke: C.card, strokeWidth: 2 }}
              connectNulls={false}
            />
          )}

          {visible.actual && (
            <Area
              type="linear"
              dataKey="actualPacKw"
              stroke={C.fact}
              strokeWidth={2}
              strokeDasharray="6 3"
              fill="url(#gradActual)"
              dot={false}
              activeDot={{ r: 4, fill: C.fact, stroke: C.card, strokeWidth: 2 }}
              connectNulls={false}
            />
          )}

          {!isHalfHour && visible.v034 && (
            <Area
              type="linear"
              dataKey="v034ShadowForecastKw"
              stroke={C.v034}
              strokeWidth={1.5}
              strokeDasharray="8 4"
              fill="url(#gradV034)"
              dot={false}
              activeDot={{ r: 3, fill: C.muted, stroke: C.card, strokeWidth: 2 }}
              connectNulls={false}
            />
          )}

          {!isHalfHour && visible.v038 && (
            <Area
              type="linear"
              dataKey="v038ShadowForecastKw"
              stroke={C.v038}
              strokeWidth={2}
              strokeDasharray="4 4"
              fill="url(#gradV038)"
              dot={false}
              activeDot={{ r: 4, fill: C.v038, stroke: C.card, strokeWidth: 2 }}
              connectNulls={false}
            />
          )}

          {!isHalfHour && visible.v045 && (
            <Area
              type="linear"
              dataKey="v045HybridForecastKw"
              stroke={C.ecmwf}
              strokeWidth={2}
              fill="url(#gradV045)"
              dot={false}
              activeDot={{ r: 4, fill: C.ecmwf, stroke: C.card, strokeWidth: 2 }}
              connectNulls={false}
            />
          )}
          {visible.ecmwfShadow && (
            <Area
              type="linear"
              dataKey="ecmwfShadowForecastKw"
              stroke={C.ecmwf}
              strokeWidth={2}
              strokeDasharray="5 3"
              fill="transparent"
              dot={false}
              activeDot={{ r: 4, fill: C.ecmwf, stroke: C.card, strokeWidth: 2 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>)}

    </div>
  );
}